import React, {useEffect, useMemo, useRef} from "react";
import {Helmet} from "react-helmet";
import {defineMessages, FormattedMessage, useIntl} from "react-intl";

// Components
import Contact_img from "../../assets/svg/motor/09_motor_Agencias.svg";
import TarifasDesktopES from "../../assets/img/Tarifas_ES.png";
import TarifasDesktopEN from "../../assets/img/Tarifas_EN.png";
import WitbookerES from "../../assets/img/Witbooker_ES.png";
import WitbookerEN from "../../assets/img/Witbooker_EN.png";
import WitbookerPlusES from "../../assets/img/Witbooker_+_ES.png";
import WitbookerPlusEN from "../../assets/img/Witbooker_+_EN.png";
import WitbookerAIES from "../../assets/img/Witbooker_AI_ES.png";
import WitbookerAIEN from "../../assets/img/Witbooker_AI_EN.png";
import {useLocaleProvider} from "../../context/localeContext";

const messages = defineMessages({
  titleSEO: {
    id: "page.prices.titleSEO",
    description: "Prices Witbooking",
    defaultMessage: "Tarifas Witbooking",
  },
  descriptionSEO: {
    id: "page.prices.descriptionSEO",
    description: "Witbooking booking engine pricing plans",
    defaultMessage: "Planes y tarifas del motor de reservas Witbooking",
  },
  keywordSEO: {
    id: "page.prices.keywordSEO",
    description: "Prices, booking engine, plans",
    defaultMessage: "Tarifas, motor de reservas, planes",
  },
  breadcrumb: {
    id: "page.prices.breadcrumb",
    description: "page prices breadcrumb",
    defaultMessage: "Precios",
  },
  title: {
    id: "page.contact.pageTitle",
    description: "page contact title",
    defaultMessage: "Contacto",
  },
  intro: {
    id: "page.contact.pageIntro",
    description: "page contact intro",
    defaultMessage:
      "Contacta con nosotros si necesitas más información o una demo online del motor de reservas o de cualquiera de nuestros módulos. Si estás interesado en ser prescriptor o distribuidor estaremos encantados te informaremos de nuestro programa de partners.",
  },
  ratesTitle: {
    id: "page.hubspotContact.ratesTitle",
    description: "rates section title",
    defaultMessage: "Tarifas",
  },
});

export default function HubspotPrices() {
  const intl = useIntl();
  const localeProvider = useLocaleProvider();
  const isSelectedEnglish = useMemo(
    () => localeProvider.currentLocale === "en-US",
    [localeProvider]
  );

  const formRef = useRef(null);

  // Select images based on locale
  const TarifasDesktop = isSelectedEnglish ? TarifasDesktopEN : TarifasDesktopES;
  const Witbooker = isSelectedEnglish ? WitbookerEN : WitbookerES;
  const WitbookerPlus = isSelectedEnglish ? WitbookerPlusEN : WitbookerPlusES;
  const WitbookerAI = isSelectedEnglish ? WitbookerAIEN : WitbookerAIES;

  useEffect(() => {
    const HUBSPOT_FORM_ID_ENGLISH = "5febc1b8-8678-4bbc-a611-3670c615ffc4";
    const HUBSPOT_FORM_ID_SPANISH = "bc7ba1d8-3623-4359-8033-c222e7c28f68";

    const setupForm = () => {
      if (window.hbspt) {
        if (formRef.current) {
          formRef.current.innerHTML = "";
        }

        const formId = isSelectedEnglish
          ? HUBSPOT_FORM_ID_ENGLISH
          : HUBSPOT_FORM_ID_SPANISH;

        window.hbspt.forms.create({
          portalId: "20410676",
          formId: formId,
          region: "na1",
          target: "#hubspot-form-prices",
        });
      }
    };

    const existingScript = document.querySelector('script[src="//js.hsforms.net/forms/embed/v2.js"]');

    if (existingScript || window.hbspt) {
      setupForm();
    } else {
      const script = document.createElement("script");
      script.src = "//js.hsforms.net/forms/embed/v2.js";
      script.charset = "utf-8";
      script.type = "text/javascript";
      document.body.appendChild(script);

      script.onload = () => {
        setupForm();
      };
    }

    return () => {
      if (formRef.current) {
        formRef.current.innerHTML = "";
      }
    };
  }, [isSelectedEnglish]);

  return (
    <>
      <Helmet>
        <title>{intl.formatMessage(messages.titleSEO)}</title>
        <meta name="title" content={intl.formatMessage(messages.titleSEO)}/>
        <meta name="description" content={intl.formatMessage(messages.descriptionSEO)}/>
        <meta name="keywords" content={intl.formatMessage(messages.keywordSEO)}/>

        <meta property="og:type" content="website"/>
        <meta property="og:url" content="https://metatags.io/"/>
        <meta property="og:title" content={intl.formatMessage(messages.titleSEO)}/>
        <meta property="og:description" content={intl.formatMessage(messages.descriptionSEO)}/>
        <meta property="og:image"
              content="https://metatags.io/assets/meta-tags-16a33a6a8531e519cc0936fbba0ad904e52d35f34a46c97a2c9f6f7dd7d336f2.png"/>

        <meta property="twitter:card" content="summary_large_image"/>
        <meta property="twitter:url" content="https://metatags.io/"/>
        <meta property="twitter:title" content={intl.formatMessage(messages.titleSEO)}/>
        <meta property="twitter:description" content={intl.formatMessage(messages.descriptionSEO)}/>
        <meta property="twitter:image"
              content="https://metatags.io/assets/meta-tags-16a33a6a8531e519cc0936fbba0ad904e52d35f34a46c97a2c9f6f7dd7d336f2.png"/>
      </Helmet>
      <section className="contact-page responsive-multimedia-off">
        <div className="wrapper">
          <div className="wrap-contact-page">
            <section className="rates-visual-section">
              <div className="rates-visual-container">
                <h2 className="rates-visual-title">
                  <FormattedMessage {...messages.ratesTitle} />
                </h2>
                <div className="rates-images">
                  <img
                    src={TarifasDesktop}
                    alt="Witbooking Rates"
                    className="rates-image-desktop"
                  />
                  <div className="rates-images-mobile">
                    <img
                      src={Witbooker}
                      alt="Witbooker"
                      className="rates-image-mobile"
                    />
                    <img
                      src={WitbookerPlus}
                      alt="Witbooker Plus"
                      className="rates-image-mobile"
                    />
                    <img
                      src={WitbookerAI}
                      alt="Witbooker AI"
                      className="rates-image-mobile"
                    />
                  </div>
                </div>
              </div>
            </section>
            <header className="contact-page-header">
              <div className="wrap-contact-header">
                <h1 className="contact-header-title">
                  <FormattedMessage {...messages.title} />
                </h1>
              </div>
            </header>
            <div className="contact-page-info">
              <div className="wrap-contact-info">
                <p className="contact-info-text">
                  <FormattedMessage {...messages.intro} />
                </p>
              </div>
            </div>
            <div className="contact-form-with-image">
              <div className="wrap-contact-form">
                <div className="contact-form-fields">
                  <div>
                    <div
                      ref={formRef}
                      id="hubspot-form-prices"
                      className="hubspot-form contact-page-form"
                    >
                    </div>
                  </div>
                </div>
              </div>
              <div className="contact-image-container">
                <img
                  src={Contact_img}
                  alt="Contact support"
                  className="contact-support-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

